/**
 * ⟦A-145 · C20⟧ `ChoixFlottants` : les réponses du joueur posées sur le décor, sans panneau
 * (la BEAUTÉ de la maquette D reprise dans la synthèse E). Trois variantes, un seul composant :
 *
 * - `"plat"` : un bouton texte plein (« Continuer → », « ← Retour »…), un simple habillage de
 *   `.bouton`, pour que les trois familles de réponse passent par le même fichier.
 * - `"portrait"` : la paire de portraits-boutons (créatrice/créateur), un vrai groupe de
 *   `<input type="radio">` (dans un `<label>`), donc Entrée, flèches et focus clavier marchent
 *   sans rien recoder.
 * - `"reglage"` : une paire de réglage (deux boutons côte à côte, l'actif en violet), même
 *   mécanique radio, rendue dans un `<fieldset>` pour garder la légende accessible.
 *
 * Le halo de focus est celui d'⟦A-144 · C19, point 0⟧ : `:has(input:focus-visible)` posé sur le
 * `<label>`/`<fieldset>` plutôt que sur l'input (masqué en `opacity: 0`), sinon invisible au
 * clavier quelle que soit sa couleur (voir `app.css`, section ⟦A-145 · C20⟧).
 */

import type { ReactNode } from "react";

import { IconeAsset } from "./IconeAsset";

interface OptionPortrait {
  id: string;
  label: string;
  portrait: string;
  portraitRepli?: ReactNode;
  actif: boolean;
  autoFocus?: boolean;
}

interface OptionReglage {
  id: string;
  titre: string;
  sousTitre: string;
  actif: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  title?: string;
}

type ChoixFlottantsProps =
  | {
      variante: "plat";
      label: ReactNode;
      style?: "primaire" | "discret";
      type?: "button" | "submit";
      disabled?: boolean;
      onClick?: () => void;
    }
  | {
      variante: "portrait";
      nom: string;
      options: readonly OptionPortrait[];
      onChange: (id: string) => void;
    }
  | {
      variante: "reglage";
      nom: string;
      accroche: string;
      options: readonly OptionReglage[];
      onChange: (id: string) => void;
    };

export function ChoixFlottants(props: ChoixFlottantsProps) {
  if (props.variante === "plat") {
    const { label, style = "primaire", type = "button", disabled, onClick } = props;
    return (
      <button type={type} className={`bouton ${style} grand`} disabled={disabled} onClick={onClick}>
        {label}
      </button>
    );
  }

  if (props.variante === "portrait") {
    const { nom, options, onChange } = props;
    return (
      <div className="paire-portraits">
        {options.map((o) => (
          <label
            key={o.id}
            className={`bouton bouton-portrait ${o.actif ? "primaire actif" : "discret"}`}
          >
            <input
              type="radio"
              name={nom}
              checked={o.actif}
              autoFocus={o.autoFocus}
              onChange={() => onChange(o.id)}
            />
            <IconeAsset
              src={o.portrait}
              alt=""
              loadingLazy={false}
              className="bouton-portrait-img"
              repli={o.portraitRepli}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    );
  }

  const { nom, accroche, options, onChange } = props;
  return (
    <fieldset className="groupe-reglage">
      <legend className="groupe-reglage-label">{accroche}</legend>
      <div className="paire-reglage">
        {options.map((o) => (
          <label
            key={o.id}
            className={`bouton bouton-reglage ${o.actif ? "primaire actif" : "discret"}`}
            title={o.title}
          >
            <input
              type="radio"
              name={nom}
              checked={o.actif}
              disabled={o.disabled}
              autoFocus={o.autoFocus}
              onChange={() => onChange(o.id)}
            />
            <strong>{o.titre}</strong>
            <span>{o.sousTitre}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
